import { Children, useMemo, useState, useCallback } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import { useResourceContext } from 'react-admin'

import { myTableColumnPreferences } from '../../../graphql/queries/user'
import { updateTableColumnPreferences, resetTableColumnPreferences } from '../../../graphql/mutations/user'

// Extract a stable field identifier from a child element
function getFieldIdentifier(child, index) {
  if (child.props?.source) return child.props.source
  if (child.key && !child.key.startsWith('.')) return child.key
  if (child.props?.label) return `label-${child.props.label}`
  // For buttons, use the component type name as identifier
  const typeName = child.type?.displayName || child.type?.name || ''
  if (typeName) return `action-${typeName}`
  return `field-${index}`
}

// Convert camelCase/snake_case to Title Case
function camelToTitleCase(str) {
  return str
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase())
    .trim()
}

// Extract a display label from a child element
function getFieldLabel(child) {
  // First check explicit label prop
  if (child.props?.label) return child.props.label

  // Try to get label from source
  if (child.props?.source) {
    let { source } = child.props
    // Handle nested paths like "profile.userType" or "funds[0].number"
    // Take the last segment after . or ]
    const lastDotIndex = source.lastIndexOf('.')
    const lastBracketIndex = source.lastIndexOf(']')
    const lastIndex = Math.max(lastDotIndex, lastBracketIndex)
    if (lastIndex !== -1) {
      source = source.substring(lastIndex + 1)
    }
    // Remove any remaining brackets
    source = source.replace(/\[.*?\]/g, '')
    if (source) {
      return camelToTitleCase(source)
    }
  }

  // Try component type name as fallback
  const typeName = child.type?.displayName || child.type?.name || ''
  if (typeName && !typeName.startsWith('_') && typeName !== 'Function') {
    // Clean up common suffixes for better display
    let cleanName = typeName.replace(/Field$/, '').replace(/Button$/, '')
    // Add space before "Button" type actions for readability
    if (typeName.endsWith('Button')) {
      cleanName = cleanName || 'Action'
    }
    return camelToTitleCase(cleanName)
  }

  return 'Column'
}

export function useColumnPreferences(children) {
  const resource = useResourceContext()
  const tableId = `mgmt-${resource}`

  // Local state for tracking changes before save
  const [localColumns, setLocalColumns] = useState(null)
  const [hasLocalChanges, setHasLocalChanges] = useState(false)

  // Fetch saved preferences
  const { data, loading, refetch } = useQuery(myTableColumnPreferences, {
    variables: { tableId },
    fetchPolicy: 'cache-and-network',
  })

  // Mutations
  const [updatePreferences, { loading: saving }] = useMutation(updateTableColumnPreferences, {
    refetchQueries: [{ query: myTableColumnPreferences, variables: { tableId } }],
  })
  const [resetPreferences, { loading: resetting }] = useMutation(resetTableColumnPreferences, {
    refetchQueries: [{ query: myTableColumnPreferences, variables: { tableId } }],
  })

  // Extract column info from Field children
  const { defaultColumns, customizableChildren } = useMemo(() => {
    const childArray = Children.toArray(children)
    const customizable = []

    childArray.forEach((child, index) => {
      // Include all valid React elements as customizable columns
      if (child && child.type) {
        customizable.push({
          child,
          field: getFieldIdentifier(child, index),
          label: getFieldLabel(child),
          originalIndex: index,
        })
      }
    })

    // Disambiguate duplicate field identifiers by appending the label.
    // This handles cases where multiple columns share the same source prop
    // (e.g., charityId used for both "Charity Name" and "Charity Number").
    const fieldCounts = new Map()
    customizable.forEach((item) => {
      fieldCounts.set(item.field, (fieldCounts.get(item.field) || 0) + 1)
    })

    const disambiguated = customizable.map((item) => {
      const field = fieldCounts.get(item.field) > 1 ? `${item.field}:${item.label}` : item.field
      return { ...item, field }
    })

    const columns = disambiguated.map((item, index) => ({
      field: item.field,
      label: item.label,
      order: index,
      visible: true,
    }))

    return {
      defaultColumns: columns,
      customizableChildren: disambiguated,
    }
  }, [children])

  // Merge saved preferences with default columns.
  // Uses multi-pass matching to handle field ID changes across releases:
  //   Pass 1: Exact field match
  //   Pass 2: Base-field match (handles disambiguation added/removed, e.g. "charityId" <-> "charityId:Charity")
  //   Pass 3: Label match (handles minified component names, shifted indices)
  const mergedColumns = useMemo(() => {
    const savedPrefs = localColumns || data?.myTableColumnPreferences
    if (!savedPrefs?.columns) {
      return defaultColumns
    }

    // Track which defaults have been claimed by a saved column
    const claimedDefaults = new Set()
    // Map each saved column to its matched default (if any)
    const savedToDefault = new Map()

    // Pass 1: Exact field match
    savedPrefs.columns.forEach((saved) => {
      const match = defaultColumns.find((def) => def.field === saved.field)
      if (match) {
        savedToDefault.set(saved, match)
        claimedDefaults.add(match.field)
      }
    })

    // Pass 2: Base-field match for unmatched columns
    // Handles disambiguation toggling: "charityId" <-> "charityId:Label"
    savedPrefs.columns.forEach((saved) => {
      if (savedToDefault.has(saved)) return

      const savedIsDisambiguated = saved.field.includes(':')
      const savedBase = saved.field.split(':')[0]
      const match = defaultColumns.find((def) => {
        if (claimedDefaults.has(def.field)) return false
        // Only match when one side is disambiguated and the other isn't,
        // otherwise both are disambiguated and should match by label in Pass 3
        const defIsDisambiguated = def.field.includes(':')
        if (savedIsDisambiguated === defIsDisambiguated) return false
        const defBase = def.field.split(':')[0]
        return savedBase === defBase || savedBase === def.field || saved.field === defBase
      })
      if (match) {
        savedToDefault.set(saved, match)
        claimedDefaults.add(match.field)
      }
    })

    // Pass 3: Label match for still-unmatched columns
    // Handles minified component names and shifted index-based IDs
    savedPrefs.columns.forEach((saved) => {
      if (savedToDefault.has(saved)) return
      if (!saved.label) return

      const match = defaultColumns.find((def) => !claimedDefaults.has(def.field) && def.label === saved.label)
      if (match) {
        savedToDefault.set(saved, match)
        claimedDefaults.add(match.field)
      }
    })

    // Build merged list from matched saved columns (preserving user's visibility/order)
    const existingColumns = savedPrefs.columns
      .filter((saved) => savedToDefault.has(saved))
      .map((saved) => {
        const defaultCol = savedToDefault.get(saved)
        return {
          ...saved,
          field: defaultCol.field, // Use current field ID
          label: defaultCol.label, // Always use current label from code
        }
      })

    // Add any new columns that weren't matched (appear at end, visible)
    const newColumns = defaultColumns
      .filter((def) => !claimedDefaults.has(def.field))
      .map((def, index) => ({
        ...def,
        order: existingColumns.length + index,
        visible: true,
      }))

    return [...existingColumns, ...newColumns].sort((a, b) => a.order - b.order)
  }, [data, localColumns, defaultColumns])

  // Filter and reorder children based on merged preferences
  const processedChildren = useMemo(() => {
    const visibleColumns = mergedColumns.filter((col) => col.visible)

    // Create a map of field to child
    const childMap = new Map(customizableChildren.map((item) => [item.field, item.child]))

    // Reorder visible children according to preferences
    return visibleColumns.map((col) => childMap.get(col.field)).filter(Boolean)
  }, [mergedColumns, customizableChildren])

  // Toggle column visibility (local state only)
  const toggleColumnVisibility = useCallback(
    (field) => {
      setLocalColumns((prev) => {
        const currentColumns = prev?.columns || mergedColumns
        const targetColumn = currentColumns.find((col) => col.field === field)
        const visibleCount = currentColumns.filter((col) => col.visible).length

        // Prevent hiding the last visible column
        if (visibleCount <= 1 && targetColumn?.visible) {
          return prev
        }

        const updatedColumns = currentColumns.map((col) =>
          col.field === field ? { ...col, visible: !col.visible } : col
        )
        return { columns: updatedColumns }
      })
      setHasLocalChanges(true)
    },
    [mergedColumns]
  )

  // Reorder columns (for drag-and-drop) - local state only
  const reorderColumns = useCallback(
    (result) => {
      if (!result.destination) return

      setLocalColumns((prev) => {
        const currentColumns = prev?.columns || [...mergedColumns]
        const reordered = [...currentColumns]
        const [removed] = reordered.splice(result.source.index, 1)
        reordered.splice(result.destination.index, 0, removed)

        // Update order values
        const withUpdatedOrder = reordered.map((col, index) => ({
          ...col,
          order: index,
        }))

        return { columns: withUpdatedOrder }
      })
      setHasLocalChanges(true)
    },
    [mergedColumns]
  )

  // Save preferences to server
  const savePreferences = useCallback(async () => {
    if (!localColumns?.columns) return

    const columnsToSave = localColumns.columns.map(({ field, visible, order, label }) => ({
      field,
      visible,
      order,
      label,
    }))

    await updatePreferences({
      variables: {
        data: {
          tableId,
          columns: columnsToSave,
        },
      },
    })

    setHasLocalChanges(false)
    await refetch()
  }, [localColumns, tableId, updatePreferences, refetch])

  // Reset to defaults
  const resetToDefaults = useCallback(async () => {
    await resetPreferences({
      variables: { tableId },
    })
    setLocalColumns(null)
    setHasLocalChanges(false)
    await refetch()
  }, [tableId, resetPreferences, refetch])

  // Reset local changes without saving
  const discardChanges = useCallback(() => {
    setLocalColumns(null)
    setHasLocalChanges(false)
  }, [])

  return {
    children: processedChildren,
    allColumns: mergedColumns,
    loading,
    saving: saving || resetting,
    hasLocalChanges,
    toggleColumnVisibility,
    reorderColumns,
    savePreferences,
    resetToDefaults,
    discardChanges,
  }
}
